import {
  getAttributesString,
  getAttributesStringExcluding,
  isSelfClosingTag,
  wrapWithOuterWrappers,
  hideMathAnnotations,
} from "../htmlUtils";

const FONT_SIZE_SPLIT = "6.5vmin";
const LINE_HEIGHT_SPLIT = "1.5";
const MAX_LINES_PER_PAGE = 7;
const TITLE_LINES = 1; // 每頁固定保留標題，文字區可用行數 = 每頁總行數 - 1
const SINGLE_PAGE_EXTRA_TEXT_LINES = 1; // Allow 8 total lines to stay single-page via scale
// .imageSet .abreast > div { width: 50% }：左圖右文各半，量測寬度要跟著只取一半
const TEXT_COLUMN_WIDTH_RATIO = 0.5;

// token 種類：切頁時要靠這個把跨頁的標籤重新配對（見 buildChunkFromTokens）
const TOKEN_OPEN = "open";   // 開標籤，例如 <div class="p">
const TOKEN_CLOSE = "close"; // 閉標籤，例如 </div>
const TOKEN_ATOM = "atom";   // 不可再切的單位：字元、英文單字、<br> 等自閉標籤

/**
 * Splits Image_108_2 content — 每一頁都保留上方標題（.title）與左欄圖片（.img），
 * 只有右欄文字被切頁（驗收標準：每個分頁的左邊圖片及上方標題要留著）。
 *
 * 因此每頁版面完全一致：標題 1 行 + 右欄文字 6 行 = 每頁 7 行（MAX_LINES_PER_PAGE），
 * 量測寬度也一律取容器的一半（右欄寬度），不再區分首頁／次頁。
 *
 * HTML structure:
 *   .imageSet > .title + .abreast > .img + .text(outer) > .text(inner, actual content)
 *
 * splitTarget = inner .text (if nested) or outer .text.
 * splitTarget 底下的巢狀元素（例如分段用的 <div>）會被拆成開／閉標籤兩個 token，
 * 切點可能落在元素內部；buildChunkFromTokens 負責在頁尾補齊、頁首重開，
 * 確保每一頁的 chunkHtml 都是成對完整的 HTML（NBK-1669）。
 */
export const splitSynopsisIntoPages_Image108_2 = async (
  htmlString,
  textColumnHeight,
  pageSize,
  referenceContainer
) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  const imageSetEl = doc.body.querySelector('.imageSet');
  if (!imageSetEl) return { htmlString, totalPages: 1 };

  const titleEl = imageSetEl.querySelector(":scope > .title");
  const abreastEl = imageSetEl.querySelector(".abreast");
  if (!abreastEl) return { htmlString, totalPages: 1 };

  const imgEl = abreastEl.querySelector(".img");

  // Outer .text: height:90% container, may contain <br> wrappers around the inner .text.
  // Inner .text: where actual readable content lives (child of outer .text).
  const outerTextEl = abreastEl.querySelector(".text");
  if (!outerTextEl) return { htmlString, totalPages: 1 };
  const innerTextEl = outerTextEl.querySelector(".text");
  // Split only the innermost text container's children — this avoids splitting wrapper div
  // open/close tags across page boundaries which would produce malformed HTML.
  const splitTarget = innerTextEl || outerTextEl;

  const outerWrappers = getOuterWrappers(doc, imageSetEl);
  const pageMeasure = createMeasureContainer(referenceContainer, TEXT_COLUMN_WIDTH_RATIO);
  const appendTarget = document.getElementById("exContainer") || document.body;
  appendTarget.appendChild(pageMeasure);

  try {
    const pageContainer = pageMeasure._innerContainer;
    void pageContainer.offsetHeight;

    const cs = window.getComputedStyle(pageContainer);
    const measuredLineHeight =
      parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5;
    // 每頁都有標題，扣掉標題行後即為右欄文字可用行數（pageSize 為每頁總行數）
    const linesPerPage = Math.max(pageSize || MAX_LINES_PER_PAGE, MAX_LINES_PER_PAGE);
    const textLinesPerPage = Math.max(linesPerPage - TITLE_LINES, 1);
    const pageMaxHeight = textLinesPerPage * measuredLineHeight;
    const singlePageMaxHeight =
      (textLinesPerPage + SINGLE_PAGE_EXTRA_TEXT_LINES) * measuredLineHeight;

    // Rule matrix:
    // 1) 每頁都有標題 => 文字區可用 6 行（每頁合計 7 行）。
    // 2) 文字剛好 7 行（含標題 8 行）時維持單頁，交給 adjustContentScale 縮放吸收。
    // 3) 8 行以上才分頁；每一頁都重複標題與左欄圖片，版面與第一頁一致。
    const tokens = collectTokens(splitTarget);
    if (!innerTextEl) {
      // Single-layer case: strip leading/trailing <br> and whitespace tokens.
      // These are HTML source formatting artifacts that would otherwise inflate
      // the measured height by one line and reduce content capacity per page.
      while (tokens.length > 0 && isBrOrWhitespace(tokens[0])) tokens.shift();
      while (tokens.length > 0 && isBrOrWhitespace(tokens[tokens.length - 1])) tokens.pop();
    }

    const textChunks = [];
    if (tokens.length === 0) return { htmlString, totalPages: 1 };

    pageContainer.innerHTML = tokensToHtml(tokens);
    void pageContainer.offsetHeight;
    if (pageContainer.scrollHeight <= singlePageMaxHeight + 1) {
      const singlePageHtml = wrapWithOuterWrappers(
        `<div class="synopsis-page">${imageSetEl.outerHTML}</div>`,
        outerWrappers,
        doc
      );
      return {
        htmlString: normalizeSynopsisPageSiblings(singlePageHtml),
        totalPages: 1
      };
    }

    // 每頁的可用行數與量測寬度都相同，故所有頁面共用同一個 chunk 迴圈。
    // openStack 帶著「上一頁結尾還沒關的標籤」，讓下一頁把它們重新開回來。
    let consumedTokenCount = 0;
    let openStack = [];
    while (consumedTokenCount < tokens.length) {
      const chunkResult = buildChunkFromTokens(
        tokens.slice(consumedTokenCount),
        pageContainer,
        pageMaxHeight,
        openStack
      );
      if (!chunkResult.chunkHtml || chunkResult.consumedCount <= 0) break;
      textChunks.push(chunkResult.chunkHtml);
      consumedTokenCount += chunkResult.consumedCount;
      openStack = chunkResult.openStack;
    }

    if (textChunks.length === 0) return { htmlString, totalPages: 1 };

    const imageSetClass = imageSetEl.className;
    const imgHtml = imgEl ? imgEl.outerHTML : '';
    const titleHtml = titleEl ? titleEl.outerHTML : '';
    const outerTextClass = outerTextEl.className;
    const innerTextClass = innerTextEl ? innerTextEl.className : null;

    // 每一頁都重建完整版面：標題 + 左欄圖片 + 右欄該頁文字，
    // 版面在頁與頁之間完全一致（不再有「第二頁沒有圖片／標題」的情況）。
    const pageDivs = textChunks.map((chunkHtml) => {
      // Reconstruct text column — no raw <br> wrappers from the original source,
      // keeping the correct nesting depth so CSS selectors still match.
      // 內層 .text 也吃得到 `.imageSet .abreast .text { height:90% }`，兩層相乘只剩
      // 81% 高度，量到的 6 行會被切掉最後一行；分頁輸出這裡把內層還原成滿版高度。
      const textHtml = innerTextClass
        ? `<div class="${outerTextClass}"><div class="${innerTextClass}" style="height:100%">${chunkHtml}</div></div>`
        : `<div class="${outerTextClass}">${chunkHtml}</div>`;
      const inner = `<div class="${imageSetClass}">${titleHtml}<div class="abreast">${imgHtml}${textHtml}</div></div>`;
      return `<div class="synopsis-page">${inner}</div>`;
    });

    const wrappedHtml = wrapWithOuterWrappers(pageDivs.join(''), outerWrappers, doc);
    return {
      htmlString: normalizeSynopsisPageSiblings(wrappedHtml),
      totalPages: textChunks.length,
    };
  } finally {
    if (pageMeasure.parentNode) {
      pageMeasure.parentNode.removeChild(pageMeasure);
    }
  }
};

const createMeasureContainer = (referenceContainer, widthRatio = 1) => {
  const measureContainer = document.createElement('div');
  const cs = window.getComputedStyle(referenceContainer);

  Object.assign(measureContainer.style, {
    position: 'absolute',
    visibility: 'hidden',
    top: '-9999px',
    left: '-9999px',
    overflow: 'visible',
    width: (referenceContainer.offsetWidth * widthRatio) + 'px',
    fontFamily: cs.fontFamily,
    fontSize: FONT_SIZE_SPLIT,
    lineHeight: LINE_HEIGHT_SPLIT,
  });

  const innerContainer = document.createElement('div');
  measureContainer.appendChild(innerContainer);
  measureContainer._innerContainer = innerContainer;

  // 攔截 _innerContainer 的 innerHTML setter，每次寫入後自動移除 MathML annotation
  const originalDescriptor = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML');
  Object.defineProperty(innerContainer, 'innerHTML', {
    get() { return originalDescriptor.get.call(this); },
    set(html) {
      originalDescriptor.set.call(this, html);
      hideMathAnnotations(this);
    },
    configurable: true,
  });

  return measureContainer;
};

/**
 * 從 token 陣列切出一頁份量的 HTML。
 *
 * 切點會落在元素內部（分段用的 <div> 開／閉標籤是兩個獨立 token），若直接 join，
 * 這一頁會留下沒關的開標籤、下一頁會以孤兒 </div> 開頭。孤兒閉標籤會提早關掉
 * 外層 .text，把後半段文字擠成 .abreast 的第三個 flex item —— 就是 NBK-1669
 * 「左欄圖片被壓小、右欄文字被拆成兩段」的成因。
 *
 * 因此這裡維護一個開標籤堆疊：
 *   1. 進來時先用 openStack 把上一頁還開著的標籤重新開一次（維持原本的樣式與縮排）
 *   2. 頁尾把還開著的標籤補上閉標籤，讓這一頁的 HTML 自成完整結構
 *   3. 回傳當下的堆疊，交給下一頁接手
 * 量測用的 HTML 與輸出完全相同，所以行高判斷不會因為補標籤而失準。
 *
 * @param {Array} tokens 尚未消化的 token
 * @param {HTMLElement} measureContainer 離屏量測容器
 * @param {number} maxHeight 單頁文字區可用高度（px）
 * @param {Array} openStack 上一頁結束時仍未關閉的開標籤 token
 * @returns {{chunkHtml: string, consumedCount: number, openStack: Array}}
 */
const buildChunkFromTokens = (tokens, measureContainer, maxHeight, openStack = []) => {
  if (!tokens.length) return { chunkHtml: "", consumedCount: 0, openStack };

  const prefix = tokensToHtml(openStack);
  const closingFor = (stack) =>
    stack.map((t) => `</${t.tag}>`).reverse().join("");
  const htmlFor = (accepted, stack) =>
    prefix + tokensToHtml(accepted) + closingFor(stack);

  const applyToken = (stack, token) => {
    if (token.kind === TOKEN_OPEN) return stack.concat([token]);
    if (token.kind === TOKEN_CLOSE) return stack.slice(0, -1);
    return stack;
  };

  // 頁尾停在開標籤只會產生一個空元素（白白吃掉一行），把它退回給下一頁。
  const finish = (accepted, stack) => {
    let kept = accepted;
    let keptStack = stack;
    while (kept.length > 1 && kept[kept.length - 1].kind === TOKEN_OPEN) {
      kept = kept.slice(0, -1);
      keptStack = keptStack.slice(0, -1);
    }
    return {
      chunkHtml: htmlFor(kept, keptStack),
      consumedCount: kept.length,
      openStack: keptStack,
    };
  };

  let accepted = [];
  let stack = openStack;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const nextStack = applyToken(stack, token);

    measureContainer.innerHTML = htmlFor(accepted.concat([token]), nextStack);
    void measureContainer.offsetHeight;

    if (measureContainer.scrollHeight > maxHeight + 1) {
      if (accepted.length === 0) {
        // 單一 token 就超高：仍然消化它，避免無限迴圈
        return {
          chunkHtml: htmlFor([token], nextStack),
          consumedCount: 1,
          openStack: nextStack,
        };
      }
      return finish(accepted, stack);
    }

    accepted = accepted.concat([token]);
    stack = nextStack;
  }

  return finish(accepted, stack);
};

// Exclude inline/paragraph tags that cannot legally contain block elements.
// Wrapping synopsis-page divs inside <p> causes the browser to auto-close the <p>,
// producing stray empty <p> nodes and broken structure.
const EXCLUDE_WRAPPER_TAGS = new Set(["P", "SPAN", "A", "B", "I", "EM", "STRONG", "FONT"]);
const PAGE_WRAPPER_CLASS = "synopsis-page";

/**
 * 由內而外收集 .imageSet 的外層包裝元素。
 * 回傳共用 htmlUtils.wrapWithOuterWrappers 所需的 info 物件
 * （{ tagName, id, className, attributes }），而非 live element，
 * 讓本策略與 PlainText / Topic / Simple 共用同一套包裝實作。
 */
const getOuterWrappers = (doc, imageSetEl) => {
  const wrappers = [];
  let current = imageSetEl.parentElement;
  while (current && current !== doc.body && current !== doc.documentElement) {
    // If input already contains paginated pages, stop here to avoid
    // wrapping newly generated pages with previous .synopsis-page wrappers.
    if (hasClass(current, PAGE_WRAPPER_CLASS)) {
      break;
    }
    if (!EXCLUDE_WRAPPER_TAGS.has(current.tagName)) {
      wrappers.push({
        tagName: current.tagName.toLowerCase(),
        id: current.id || '',
        className: typeof current.className === 'string' ? current.className : '',
        attributes: getAttributesStringExcluding(current, ['class', 'id']),
      });
    }
    current = current.parentElement;
  }
  return wrappers; // innermost first (direct parent first)
};

const hasClass = (element, className) => {
  if (!element || !className) return false;
  if (element.classList) return element.classList.contains(className);
  const rawClass = typeof element.className === "string" ? element.className : "";
  return rawClass.split(/\s+/).includes(className);
};

// Defensive normalization:
// if any upstream wrapper accidentally creates nested .synopsis-page,
// lift the inner page(s) out so all pages are siblings.
const normalizeSynopsisPageSiblings = (htmlString) => {
  if (!htmlString || htmlString.indexOf(PAGE_WRAPPER_CLASS) === -1) {
    return htmlString;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  const nestedPages = Array.from(doc.body.querySelectorAll(".synopsis-page .synopsis-page"));

  if (nestedPages.length === 0) return htmlString;

  nestedPages.forEach((pageEl) => {
    let current = pageEl;
    while (current.parentElement && hasClass(current.parentElement, PAGE_WRAPPER_CLASS)) {
      const parentPage = current.parentElement;
      if (!parentPage.parentNode) break;
      parentPage.parentNode.insertBefore(current, parentPage.nextSibling);
    }
  });

  return doc.body.innerHTML;
};

const collectTokens = (el) => {
  const tokens = [];
  const wordPattern = /[\w'-]+/g;

  const collect = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      let lastIndex = 0;
      let match;
      wordPattern.lastIndex = 0;
      while ((match = wordPattern.exec(text)) !== null) {
        for (const char of text.substring(lastIndex, match.index)) {
          tokens.push({ content: char, kind: TOKEN_ATOM });
        }
        // English word as atomic unit
        tokens.push({ content: match[0], kind: TOKEN_ATOM });
        lastIndex = match.index + match[0].length;
      }
      for (const char of text.substring(lastIndex)) {
        tokens.push({ content: char, kind: TOKEN_ATOM });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      if (isSelfClosingTag(node.tagName)) {
        tokens.push({ content: node.outerHTML, kind: TOKEN_ATOM });
        return;
      }
      const tag = node.tagName.toLowerCase();
      tokens.push({
        content: `<${tag}${getAttributesString(node)}>`,
        kind: TOKEN_OPEN,
        tag,
      });
      Array.from(node.childNodes).forEach(collect);
      tokens.push({ content: `</${tag}>`, kind: TOKEN_CLOSE, tag });
    }
  };

  Array.from(el.childNodes).forEach(collect);
  return tokens;
};

const tokensToHtml = (tokens) => tokens.map(t => t.content).join('');

const isBrOrWhitespace = (token) =>
  token.content === '<br>' || token.content.trim() === '';
